import type { AppointmentResource, AppointmentTypeResource, ContactResource } from '@majuscule/contracts';
import { bookingLink, CompanyIdCodec, DealIdCodec } from '@majuscule/domain';
import { CalendarPlus, ExternalLink, Video } from 'lucide-react';
import type { JSX } from 'react';
import { formatInstant } from '../../lib/crm';
import { MiniCell, MiniList, MiniRow } from './shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHead } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Rendez-vous Cal.com d'une entreprise ou d'une affaire (lot 22) : liste, et
 * « Planifier » qui ouvre la page de réservation du type choisi, préremplie
 * avec le contact et portant l'entreprise et l'affaire en métadonnées : le
 * webhook rattache la réservation tout seul.
 */
export function AppointmentsCard({
  companyId,
  dealId,
  contact,
  appointments,
  types,
}: {
  companyId: string;
  dealId: string | null;
  /** Contact prérempli (le principal, ou le premier de l'affaire). */
  contact: ContactResource | null;
  appointments: readonly AppointmentResource[];
  types: readonly AppointmentTypeResource[];
}): JSX.Element {
  const company = CompanyIdCodec.parse(companyId);
  const deal = dealId === null ? null : DealIdCodec.parse(dealId);
  const link = (type: AppointmentTypeResource): string | null =>
    company.ok && (deal === null || deal.ok)
      ? bookingLink(type, {
          companyId: company.value,
          dealId: deal === null || !deal.ok ? null : deal.value,
          attendee:
            contact === null
              ? null
              : { name: `${contact.firstName} ${contact.lastName}`, email: contact.email },
        })
      : null;
  const now = Date.now();
  const upcoming = appointments.filter((a) => a.status === 'scheduled' && new Date(a.endAt).getTime() >= now);
  const past = appointments.filter((a) => !upcoming.includes(a)).reverse();

  return (
    <Card>
      <CardHead
        title="Rendez-vous"
        description="Pris sur Cal.com"
        actions={
          types.length === 0 ? undefined : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm">
                  <CalendarPlus aria-hidden="true" />
                  Planifier
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {types.map((type) => {
                  const href = link(type);
                  return (
                    <DropdownMenuItem
                      key={type.id}
                      disabled={href === null}
                      onSelect={() => {
                        if (href !== null) {
                          window.open(href, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      {type.name}
                      <ExternalLink className="ml-auto size-3.5 text-muted-foreground" aria-hidden="true" />
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
      />
      <CardContent>
        {appointments.length === 0 ? (
          <p className="m-0 text-[13px] text-muted-foreground">
            {types.length === 0
              ? 'Aucun type de rendez-vous configuré (Configuration › Rendez-vous).'
              : 'Aucun rendez-vous : « Planifier » ouvre la page Cal.com préremplie.'}
          </p>
        ) : (
          <MiniList>
            {[...upcoming, ...past].map((appointment) => (
              <AppointmentRow key={appointment.id} appointment={appointment} past={!upcoming.includes(appointment)} />
            ))}
          </MiniList>
        )}
      </CardContent>
    </Card>
  );
}

function AppointmentRow({ appointment, past }: { appointment: AppointmentResource; past: boolean }): JSX.Element {
  const cancelled = appointment.status === 'cancelled';
  return (
    <MiniRow>
      <MiniCell
        primary={
          <span className={cancelled ? 'text-muted-foreground line-through' : undefined}>
            {appointment.typeName ?? appointment.title}
          </span>
        }
        secondary={`${formatInstant(appointment.startAt)}${appointment.attendeeName !== null ? ` · ${appointment.attendeeName}` : ''}`}
      />
      <div className="flex items-center justify-end gap-1">
        {cancelled ? (
          <Badge variant="danger">Annulé</Badge>
        ) : past ? (
          <Badge variant="neutral">Passé</Badge>
        ) : (
          <Badge variant="info">À venir</Badge>
        )}
        {!cancelled && !past && appointment.meetingUrl !== null ? (
          <Button asChild variant="ghost" size="icon-sm">
            <a href={appointment.meetingUrl} target="_blank" rel="noreferrer noopener" aria-label="Rejoindre la visio">
              <Video aria-hidden="true" />
            </a>
          </Button>
        ) : null}
      </div>
    </MiniRow>
  );
}
